/**
 * Room parameter editing — shared by the space-detail page (pencil on each
 * room tile) and the room page itself (pencil next to the room name; MU
 * feedback 2026-07: "za název místnosti přidejte ikonu tužky pro editaci
 * parametrů místnosti stejně jako na předchozí stránce s prostorem").
 *
 * `RoomVisibilityFields` + `buildRoomAclPayload` live here too so the
 * create-room modal (space-detail) and this edit modal can't drift apart in
 * how they serialize the inherit-vs-explicit ACL choice.
 */

import { Visibility } from '@polygraf/shared'
import { useState } from 'react'
import { Alert, Button, Modal, Select, Textarea, TextField, Toggle } from '@/components/ui'
import { parseLineList } from '@/routes/spaces'
import { trpc } from '@/trpc/client'

/** The subset of a room row the edit modal needs (rooms.get / spaces.get). */
export interface EditableRoom {
	id: string
	name: string
	description?: string | null
	isActive: boolean
	visibility?: Visibility
	inheritAccess?: boolean
	allowedIpRanges?: string[]
	allowedDomains?: string[]
}

/**
 * Inherit-vs-explicit access controls for a room (UAT F2 / B3). While the
 * inherit toggle is ON the visibility select and ACL textareas are hidden
 * behind a short explanation. While OFF the visibility select shows, plus the
 * tier's ACL inputs — PRIVATE consults `allowedDomains` (wildcard email
 * membership, e.g. `*.muni.cz`), IP_RESTRICTED consults both lists, other
 * tiers neither.
 */
export function RoomVisibilityFields({
	inheritAccess,
	setInheritAccess,
	visibility,
	setVisibility,
	allowedIpRanges,
	setAllowedIpRanges,
	allowedDomains,
	setAllowedDomains,
	t,
}: {
	inheritAccess: boolean
	setInheritAccess: (v: boolean) => void
	visibility: Visibility
	setVisibility: (v: Visibility) => void
	allowedIpRanges: string
	setAllowedIpRanges: (v: string) => void
	allowedDomains: string
	setAllowedDomains: (v: string) => void
	t: (key: string) => string
}) {
	return (
		<>
			<Toggle
				label={t('rooms.inheritAccess.label')}
				checked={inheritAccess}
				onChange={(e) => setInheritAccess(e.target.checked)}
			/>

			{inheritAccess ? (
				<p className="text-xs text-base-content/60">{t('rooms.inheritAccess.explanation')}</p>
			) : (
				<>
					<Select
						label={t('spaceDetail.modal.visibility')}
						value={visibility}
						onChange={(e) => setVisibility(e.target.value as Visibility)}
					>
						<option value={Visibility.PUBLIC}>{t('spaces.visibility.public')}</option>
						<option value={Visibility.AUTHENTICATED}>{t('spaces.visibility.authenticated')}</option>
						<option value={Visibility.PRIVATE}>{t('spaces.visibility.private')}</option>
						<option value={Visibility.IP_RESTRICTED}>{t('spaces.visibility.ipRestricted')}</option>
					</Select>

					{visibility === Visibility.PRIVATE && (
						<>
							<Textarea
								label={t('spaces.createModal.allowedDomainsLabel')}
								value={allowedDomains}
								onChange={(e) => setAllowedDomains(e.target.value)}
								placeholder={t('spaces.createModal.allowedDomainsPlaceholder')}
								rows={2}
							/>
							<p className="text-xs text-base-content/60">{t('rooms.privateDomainsHint')}</p>
						</>
					)}

					{visibility === Visibility.IP_RESTRICTED && (
						<>
							<Textarea
								label={t('spaces.createModal.allowedIpRangesLabel')}
								value={allowedIpRanges}
								onChange={(e) => setAllowedIpRanges(e.target.value)}
								placeholder={t('spaces.createModal.allowedIpRangesPlaceholder')}
								rows={3}
							/>
							<Textarea
								label={t('spaces.createModal.allowedDomainsLabel')}
								value={allowedDomains}
								onChange={(e) => setAllowedDomains(e.target.value)}
								placeholder={t('spaces.createModal.allowedDomainsPlaceholder')}
								rows={2}
							/>
							<p className="text-xs text-base-content/60">{t('rooms.visibilityHint')}</p>
						</>
					)}
				</>
			)}
		</>
	)
}

/**
 * Room ACL mutation payload for rooms.create / rooms.update. While inheriting,
 * visibility and the ACL lists stay undefined so the server leaves the stored
 * (inert) values untouched and `inheritAccess: true` alone decides; with an
 * explicit ACL the lists are persisted per tier and cleared otherwise, so no
 * stale rules linger after a tier switch.
 */
export function buildRoomAclPayload(
	inheritAccess: boolean,
	visibility: Visibility,
	allowedIpRanges: string,
	allowedDomains: string,
): {
	inheritAccess: boolean
	visibility?: Visibility
	allowedIpRanges?: string[]
	allowedDomains?: string[]
} {
	if (inheritAccess) {
		return { inheritAccess }
	}
	const isIpRestricted = visibility === Visibility.IP_RESTRICTED
	const consultsDomains = isIpRestricted || visibility === Visibility.PRIVATE
	return {
		inheritAccess,
		visibility,
		allowedIpRanges: isIpRestricted ? parseLineList(allowedIpRanges) : [],
		allowedDomains: consultsDomains ? parseLineList(allowedDomains) : [],
	}
}

export function EditRoomModal({
	room,
	onClose,
	onSuccess,
	t,
}: {
	room: EditableRoom
	onClose: () => void
	onSuccess: () => void
	t: (key: string) => string
}) {
	const [name, setName] = useState(room.name)
	const [description, setDescription] = useState(room.description || '')
	const [isActive, setIsActive] = useState(room.isActive)
	// `?? false` is the safe fallback should the flag ever be missing from a
	// stale payload: it re-asserts the room's stored visibility (also in the
	// payload) rather than silently widening an explicit ACL to "inherit".
	const [inheritAccess, setInheritAccess] = useState(room.inheritAccess ?? false)
	const [visibility, setVisibility] = useState<Visibility>(room.visibility ?? Visibility.PUBLIC)
	const [allowedIpRanges, setAllowedIpRanges] = useState((room.allowedIpRanges ?? []).join('\n'))
	const [allowedDomains, setAllowedDomains] = useState((room.allowedDomains ?? []).join('\n'))
	const [submitting, setSubmitting] = useState(false)

	const updateRoomMutation = trpc.rooms.update.useMutation({
		onSuccess,
	})

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault()
		setSubmitting(true)
		try {
			await updateRoomMutation.mutateAsync({
				id: room.id,
				name,
				description: description || null,
				isActive,
				...buildRoomAclPayload(inheritAccess, visibility, allowedIpRanges, allowedDomains),
			})
		} finally {
			setSubmitting(false)
		}
	}

	return (
		<Modal
			open
			onClose={onClose}
			title={t('spaceDetail.modal.editRoom')}
			closeLabel={t('common.close')}
			size="md"
			footer={
				<>
					<Button type="button" variant="ghost" onClick={onClose}>
						{t('common.cancel')}
					</Button>
					<Button type="submit" form="edit-room-form" loading={submitting} disabled={!name}>
						{t('common.save')}
					</Button>
				</>
			}
		>
			<form id="edit-room-form" onSubmit={handleSubmit} className="space-y-4">
				<TextField
					type="text"
					label={t('spaceDetail.modal.name')}
					value={name}
					onChange={(e) => setName(e.target.value)}
					required
					autoFocus
				/>

				<Textarea
					label={t('spaceDetail.modal.description')}
					value={description}
					onChange={(e) => setDescription(e.target.value)}
				/>

				<Toggle
					label={t('spaceDetail.modal.active')}
					checked={isActive}
					onChange={(e) => setIsActive(e.target.checked)}
				/>

				<RoomVisibilityFields
					inheritAccess={inheritAccess}
					setInheritAccess={setInheritAccess}
					visibility={visibility}
					setVisibility={setVisibility}
					allowedIpRanges={allowedIpRanges}
					setAllowedIpRanges={setAllowedIpRanges}
					allowedDomains={allowedDomains}
					setAllowedDomains={setAllowedDomains}
					t={t}
				/>

				{updateRoomMutation.error && <Alert tone="error">{updateRoomMutation.error.message}</Alert>}
			</form>
		</Modal>
	)
}
